import React, { forwardRef } from "react";
import { get } from "lodash";
import { formatRaceDate } from "@tvg/formatter/dates";
import {
  Header,
  Icon,
  Paragraph,
  Tag,
  useColorTokens
} from "@tvg/design-system";
import { getLength, getOdds, getPosition } from "./functions";
import { PastRaceUIProps } from "./types";
import {
  MobileCardHeader,
  MobileCord,
  TagWrapper,
  MobileCardContent,
  MobileHeaderCol,
  ExtraInfo
} from "./styled-components";
import ExtraInfoComp from "./ExtraInfo";

const PastRaceMobile = forwardRef<HTMLDivElement, PastRaceUIProps>(
  (
    {
      isVideoAvailable,
      race,
      surfaceShortname,
      surfaceCondition,
      distance,
      raceClassShortname,
      runner,
      purse,
      beatenDistance,
      beatenDistanceStatus,
      distanceFontFamily,
      surfaceConditionFontFamily,
      surfaceShortnameFontFamily
    },
    ref
  ) => {
    const colorTokens = useColorTokens();
    const raceNumber = get(race, "number");
    const raceName = get(race, "track.name");

    return (
      <MobileCord data-qa-label="runner-handicap-inline-pp-container" ref={ref}>
        <MobileCardHeader>
          <MobileHeaderCol>
            <Paragraph
              qaLabel="runner-handicap-inline-pp-position"
              color={
                runner && runner.finishPosition === 1
                  ? colorTokens.background.positive
                  : colorTokens.content.subtle
              }
              fontFamily="bold"
            >
              {getPosition(runner, race.numRunners)}
            </Paragraph>
            <Paragraph
              qaLabel="runner-handicap-inline-pp-length"
              color={colorTokens.content.subtle2}
              fontWeight={400}
              fontFamily="regular"
            >
              {getLength(beatenDistance, beatenDistanceStatus)}
            </Paragraph>
          </MobileHeaderCol>
          <MobileHeaderCol>
            <Paragraph
              qaLabel="runner-handicap-inline-pp-date"
              color={colorTokens.content.subtle2}
            >
              {formatRaceDate(race.date)}
            </Paragraph>
            <Icon
              size="s"
              name={isVideoAvailable ? "replay" : "noVideo"}
              qaLabel="runner-handicap-inline-pp-video"
              lineColor={
                race.video
                  ? colorTokens.content.link
                  : colorTokens.content.subtle
              }
              ml="space-3"
            />
            <Icon
              size="s"
              name="chevronRight"
              lineColor={colorTokens.content.link}
              ml="space-1"
            />
          </MobileHeaderCol>
        </MobileCardHeader>
        <MobileCardContent>
          <Header
            qaLabel="runner-handicap-inline-trackName"
            tag="h3"
            fontSize={14}
            lineHeight={18}
            mb="space-1"
            textTransform="capitalize"
          >
            {`${raceName} R${raceNumber}`}
          </Header>
          <ExtraInfo
            role="paragraph"
            data-qa-label="runner-handicap-inline-pp-match-conditions"
            showDefault
          >
            {runner && runner.favorite ? (
              <TagWrapper>
                <Tag
                  qaLabel="runner-handicap-inline-pp-odds-fav"
                  label={getOdds(runner)}
                  size="s"
                  variant="promo"
                />
              </TagWrapper>
            ) : (
              <Paragraph
                qaLabel="runner-handicap-inline-pp-odds"
                color={colorTokens.content.subtle}
              >
                {getOdds(runner)}
              </Paragraph>
            )}
            <span> | </span>
            <ExtraInfoComp
              defaultColor={colorTokens.content.subtle}
              distance={distance}
              surfaceShortname={surfaceShortname}
              surfaceShortnameFontFamily={surfaceShortnameFontFamily}
              distanceFontFamily={distanceFontFamily}
              surfaceConditionFontFamily={surfaceConditionFontFamily}
              surfaceCondition={surfaceCondition}
              purse={purse}
              raceClassShortname={raceClassShortname}
            />
          </ExtraInfo>
        </MobileCardContent>
      </MobileCord>
    );
  }
);

export default PastRaceMobile;
